import React from 'react';
import styled from 'styled-components';

/* ---------- styled components ---------- */
const PageBody = styled.div`
  padding: 1.5rem 0 4rem;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageHeaderLeft = styled.div`
  h1 { font-size: 1.5rem; margin-bottom: 0.25rem; }
  p { font-size: 0.875rem; color: var(--text-muted); }
`;

const ControlsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ControlSelect = styled.select`
  padding: 0.5rem 0.875rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  color: var(--text-primary);
  cursor: pointer;
  outline: none;
`;

/* AI Deep Dive */
const AIDeepDive = styled.div`
  margin-bottom: 2rem;
`;

const DeepDiveBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const DeepDiveIcon = styled.span`
  position: absolute;
  left: 1.25rem;
  color: var(--accent);
  font-size: 1.125rem;
  pointer-events: none;
`;

const DeepDiveInput = styled.input`
  width: 100%;
  padding: 0.875rem 1.25rem 0.875rem 3rem;
  background: var(--bg-surface);
  border: 2px solid var(--border);
  border-radius: 14px;
  font-family: inherit;
  font-size: 0.9375rem;
  color: var(--text-primary);
  outline: none;
  transition: all 0.3s;
  box-shadow: var(--shadow-md);

  &::placeholder { color: var(--text-muted); }
  &:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--ring), var(--shadow-lg); }
`;

const DeepDiveSuggestions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
`;

const DeepDiveChip = styled.button`
  padding: 0.375rem 0.75rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 999px;
  font-size: 0.75rem;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--accent-subtle);
  }
`;

/* KPI Strip */
const KPIStrip = styled.section`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 1024px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 640px) { grid-template-columns: repeat(2, 1fr); }
`;

const KPICard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.25rem;
  transition: box-shadow 0.2s;

  &:hover { box-shadow: var(--shadow-md); }
`;

const KPILabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
`;

const KPIValueRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.5rem;
`;

const KPIValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
`;

const KPIChange = styled.span<{ $positive?: boolean }>`
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  background: ${({ $positive }) => $positive ? 'var(--success-subtle)' : 'var(--error-subtle)'};
  color: ${({ $positive }) => $positive ? 'var(--success)' : 'var(--error)'};
`;

const Sparkline = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 28px;
  margin-top: 0.75rem;
`;

const SparkBar = styled.div<{ $h: number; $color?: string }>`
  flex: 1;
  border-radius: 2px;
  background: ${({ $color }) => $color || 'var(--accent)'};
  opacity: 0.6;
  height: ${({ $h }) => $h}%;
  transition: opacity 0.2s;

  &:hover { opacity: 1; }
`;

/* Charts */
const ChartsGrid = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const ChartCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.5rem;
`;

const ChartTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
`;

const ChartPlaceholder = styled.div`
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  font-size: 0.8125rem;
  background: var(--bg-primary);
  border-radius: 8px;
`;

/* Table */
const TableSection = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ExportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid var(--border);
  background: var(--bg-elevated);
  color: var(--text-primary);

  &:hover { background: var(--bg-inset); }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;

  th, td {
    text-align: left;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border);
  }

  th {
    font-weight: 600;
    color: var(--text-muted);
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  td { color: var(--text-primary); }

  tbody tr:hover { background: var(--bg-elevated); }
  tbody tr:last-child td { border-bottom: none; }
`;

const StatusDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  background: ${({ $color }) => $color};
  margin-right: 0.5rem;
`;

const VolBar = styled.div<{ $w: number }>`
  height: 6px;
  border-radius: 3px;
  background: var(--accent);
  width: ${({ $w }) => $w}%;
  min-width: 8px;
`;

/* ---------- component ---------- */
const Analytics: React.FC = () => (
  <PageBody className="fade-in">
    <Container>
      <PageHeader>
        <PageHeaderLeft>
          <h1>Analytics</h1>
          <p>Monitor agent performance and platform health</p>
        </PageHeaderLeft>
        <ControlsBar>
          <ControlSelect>
            <option>Last 7 days</option>
            <option>Last 24 hours</option>
            <option>Last 30 days</option>
            <option>All time</option>
          </ControlSelect>
          <ControlSelect>
            <option>All Agents</option>
            <option>Emily</option>
            <option>Martin</option>
          </ControlSelect>
        </ControlsBar>
      </PageHeader>

      <AIDeepDive>
        <DeepDiveBar>
          <DeepDiveIcon>&#x2728;</DeepDiveIcon>
          <DeepDiveInput placeholder='Ask about your analytics... "Why did errors spike last Tuesday?"' />
        </DeepDiveBar>
        <DeepDiveSuggestions>
          <DeepDiveChip>Busiest hours this week</DeepDiveChip>
          <DeepDiveChip>Top failing agents</DeepDiveChip>
          <DeepDiveChip>Compare agent performance</DeepDiveChip>
          <DeepDiveChip>Token usage breakdown</DeepDiveChip>
        </DeepDiveSuggestions>
      </AIDeepDive>

      <KPIStrip>
        <KPICard>
          <KPILabel>Total Sessions</KPILabel>
          <KPIValueRow><KPIValue>12,847</KPIValue><KPIChange $positive>+12%</KPIChange></KPIValueRow>
          <Sparkline>{[30, 45, 35, 55, 50, 70, 65, 80, 75, 85].map((h, i) => <SparkBar key={i} $h={h} />)}</Sparkline>
        </KPICard>
        <KPICard>
          <KPILabel>Active Agents</KPILabel>
          <KPIValueRow><KPIValue>24</KPIValue><KPIChange $positive>+3</KPIChange></KPIValueRow>
          <Sparkline>{[50, 55, 50, 60, 55, 65, 60, 70, 65, 75].map((h, i) => <SparkBar key={i} $h={h} $color="var(--info)" />)}</Sparkline>
        </KPICard>
        <KPICard>
          <KPILabel>Avg Response</KPILabel>
          <KPIValueRow><KPIValue>1.2s</KPIValue><KPIChange $positive>-8%</KPIChange></KPIValueRow>
          <Sparkline>{[80, 75, 70, 65, 60, 55, 50, 48, 45, 42].map((h, i) => <SparkBar key={i} $h={h} $color="var(--success)" />)}</Sparkline>
        </KPICard>
        <KPICard>
          <KPILabel>Success Rate</KPILabel>
          <KPIValueRow><KPIValue>94.2%</KPIValue><KPIChange $positive>+1.3%</KPIChange></KPIValueRow>
          <Sparkline>{[88, 89, 90, 91, 90, 92, 93, 92, 94, 94].map((h, i) => <SparkBar key={i} $h={h} $color="var(--warning)" />)}</Sparkline>
        </KPICard>
        <KPICard>
          <KPILabel>Tokens Used</KPILabel>
          <KPIValueRow><KPIValue>2.4M</KPIValue><KPIChange>+18%</KPIChange></KPIValueRow>
          <Sparkline>{[35, 40, 50, 55, 60, 65, 70, 75, 80, 85].map((h, i) => <SparkBar key={i} $h={h} $color="var(--text-muted)" />)}</Sparkline>
        </KPICard>
      </KPIStrip>

      <ChartsGrid>
        <ChartCard>
          <ChartTitle>Session Volume</ChartTitle>
          <ChartPlaceholder>Stacked bar chart — connect to API for live data</ChartPlaceholder>
        </ChartCard>
        <ChartCard>
          <ChartTitle>Success / Failure Ratio</ChartTitle>
          <ChartPlaceholder>Stacked area chart — connect to API for live data</ChartPlaceholder>
        </ChartCard>
      </ChartsGrid>

      <ChartCard style={{ marginBottom: '1.5rem' }}>
        <ChartTitle>Response Latency (p50 / p95 / p99)</ChartTitle>
        <ChartPlaceholder>Multi-line chart — connect to API for live data</ChartPlaceholder>
      </ChartCard>

      <TableSection>
        <TableHeader>
          <ChartTitle style={{ marginBottom: 0 }}>Agent Usage Breakdown</ChartTitle>
          <ExportBtn>&#x2B07; Export CSV</ExportBtn>
        </TableHeader>
        <StyledTable>
          <thead>
            <tr>
              <th>Agent</th>
              <th>Sessions</th>
              <th>Avg Latency</th>
              <th>Success Rate</th>
              <th>Volume</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><StatusDot $color="var(--success)" />Emily</td>
              <td>4,231</td>
              <td>1.1s</td>
              <td>96.2%</td>
              <td><VolBar $w={85} /></td>
            </tr>
            <tr>
              <td><StatusDot $color="var(--success)" />Emily-Menu</td>
              <td>3,102</td>
              <td>0.8s</td>
              <td>98.1%</td>
              <td><VolBar $w={62} /></td>
            </tr>
            <tr>
              <td><StatusDot $color="var(--error)" />Emily-Reservations</td>
              <td>2,847</td>
              <td>1.9s</td>
              <td>87.4%</td>
              <td><VolBar $w={57} /></td>
            </tr>
            <tr>
              <td><StatusDot $color="var(--success)" />Martin</td>
              <td>1,823</td>
              <td>1.3s</td>
              <td>95.7%</td>
              <td><VolBar $w={37} /></td>
            </tr>
            <tr>
              <td><StatusDot $color="var(--text-muted)" />Data Extractor</td>
              <td>844</td>
              <td>2.1s</td>
              <td>92.3%</td>
              <td><VolBar $w={17} /></td>
            </tr>
          </tbody>
        </StyledTable>
      </TableSection>
    </Container>
  </PageBody>
);

export default Analytics;
